import { useState, useEffect, useCallback } from 'react';
import {
    Briefcase,
    Plus,
    X,
    Star,
    ChevronDown,
    ChevronUp,
    Loader2,
    Check,
    AlertCircle,
    Sparkles
} from 'lucide-react';
import OccupationAutocomplete from './OccupationAutocomplete';
import {
    getPeopleOccupations,
    addPeopleOccupation,
    removePeopleOccupation,
    setPrimaryOccupation,
    getSkillsForOccupation,
    fetchEscoSkillsForOccupation,
    getPeopleSkills,
    togglePeopleSkill,
    fillSkillTranslation
} from '../services/skillsService';

/**
 * OccupationsSkillsPanel - Panel to manage occupations and their skills
 */
const OccupationsSkillsPanel = ({
    peopleUuid,
    language = 'fr',
    className = ''
}) => {
    const [occupations, setOccupations] = useState([]);
    const [expandedOccupation, setExpandedOccupation] = useState(null);
    const [occupationSkills, setOccupationSkills] = useState({});
    const [userSkillIds, setUserSkillIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddingOccupation, setIsAddingOccupation] = useState(false);
    const [loadingSkills, setLoadingSkills] = useState({});
    const [togglingSkill, setTogglingSkill] = useState(null);

    // Load occupations
    const loadOccupations = useCallback(async () => {
        if (!peopleUuid) return;

        setIsLoading(true);
        try {
            const data = await getPeopleOccupations(peopleUuid, language);
            setOccupations(data);

            // Also load user's selected skills
            const skillsData = await getPeopleSkills(peopleUuid, null, language);
            setUserSkillIds(skillsData.skillIds);
        } catch (error) {
            console.error('Error loading occupations:', error);
        } finally {
            setIsLoading(false);
        }
    }, [peopleUuid, language]);

    useEffect(() => {
        loadOccupations();
    }, [loadOccupations]);

    // Load skills for an occupation
    const loadSkillsForOccupation = async (occupationId) => {
        if (occupationSkills[occupationId]) return; // Already loaded

        setLoadingSkills(prev => ({ ...prev, [occupationId]: true }));

        try {
            // First check if skills exist in DB
            let skills = await getSkillsForOccupation(occupationId, language);

            // If no skills, fetch from ESCO API
            if (skills.length === 0) {
                await fetchEscoSkillsForOccupation(occupationId);
                skills = await getSkillsForOccupation(occupationId, language);
            }

            setOccupationSkills(prev => ({ ...prev, [occupationId]: skills }));
        } catch (error) {
            console.error('Error loading skills:', error);
        } finally {
            setLoadingSkills(prev => ({ ...prev, [occupationId]: false }));
        }
    };

    // Toggle occupation expansion
    const handleToggleExpand = (occupationId) => {
        if (expandedOccupation === occupationId) {
            setExpandedOccupation(null);
        } else {
            setExpandedOccupation(occupationId);
            loadSkillsForOccupation(occupationId);
        }
    };

    // Add occupation
    const handleAddOccupation = async (occupationId, occupation) => {
        if (!occupationId) return;

        const isPrimary = occupations.length === 0;
        const result = await addPeopleOccupation(peopleUuid, occupationId, isPrimary);

        if (result.success) {
            setIsAddingOccupation(false);
            loadOccupations();
        }
    };

    // Remove occupation
    const handleRemoveOccupation = async (occupationId) => {
        const result = await removePeopleOccupation(peopleUuid, occupationId);

        if (result.success) {
            setOccupations(prev => prev.filter(o => o.id !== occupationId));
            // Remove skills for this occupation from state
            setOccupationSkills(prev => {
                const { [occupationId]: _, ...rest } = prev;
                return rest;
            });
            if (expandedOccupation === occupationId) {
                setExpandedOccupation(null);
            }
        }
    };

    // Set primary occupation
    const handleSetPrimary = async (occupationId) => {
        const result = await setPrimaryOccupation(peopleUuid, occupationId);

        if (result.success) {
            setOccupations(prev => prev.map(o => ({
                ...o,
                isPrimary: o.id === occupationId
            })));
        }
    };

    // Toggle skill selection
    const handleToggleSkill = async (skillId, occupationId) => {
        setTogglingSkill(skillId);

        const isSelected = userSkillIds.includes(skillId);
        const action = isSelected ? 'remove' : 'add';

        const result = await togglePeopleSkill(peopleUuid, skillId, action, occupationId);

        if (result.success) {
            if (isSelected) {
                setUserSkillIds(prev => prev.filter(id => id !== skillId));
            } else {
                setUserSkillIds(prev => [...prev, skillId]);
            }
        }

        setTogglingSkill(null);
    };

    // Get display name
    const getDisplayName = (item) => {
        return item[`name_${language}`] || item.name_fr || item.name_en || '';
    };

    if (isLoading) {
        return (
            <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-cabaza-purple animate-spin" />
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-cabaza-purple" />
                    <h3 className="font-semibold text-gray-900">Mes m&eacute;tiers et comp&eacute;tences</h3>
                </div>
                {!isAddingOccupation && (
                    <button
                        onClick={() => setIsAddingOccupation(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-cabaza-purple hover:bg-purple-50 rounded-lg transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Ajouter
                    </button>
                )}
            </div>

            {/* Add occupation form */}
            {isAddingOccupation && (
                <div className="p-4 bg-purple-50 border-b border-purple-100">
                    <div className="flex items-start gap-2">
                        <div className="flex-1">
                            <OccupationAutocomplete
                                label=""
                                language={language}
                                placeholder="Recherchez un m&eacute;tier..."
                                onChange={handleAddOccupation}
                            />
                        </div>
                        <button
                            onClick={() => setIsAddingOccupation(false)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Occupations list */}
            <div className="divide-y divide-gray-100">
                {occupations.length === 0 && !isAddingOccupation ? (
                    <div className="p-8 text-center">
                        <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 mb-4">Aucun m&eacute;tier ajout&eacute;</p>
                        <button
                            onClick={() => setIsAddingOccupation(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-cabaza-purple text-white rounded-lg hover:bg-cabaza-purple-dark transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Ajouter mon premier m&eacute;tier
                        </button>
                    </div>
                ) : (
                    occupations.map((occupation) => (
                        <div key={occupation.id} className="group">
                            {/* Occupation header */}
                            <div
                                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => handleToggleExpand(occupation.id)}
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900">
                                            {getDisplayName(occupation)}
                                        </span>
                                        {occupation.isPrimary && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                                                <Star className="w-3 h-3" />
                                                Principal
                                            </span>
                                        )}
                                    </div>
                                    {occupationSkills[occupation.id] && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {userSkillIds.filter(id =>
                                                occupationSkills[occupation.id].some(s => s.id === id)
                                            ).length} / {occupationSkills[occupation.id].length} comp&eacute;tences s&eacute;lectionn&eacute;es
                                        </p>
                                    )}
                                </div>

                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {!occupation.isPrimary && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSetPrimary(occupation.id);
                                            }}
                                            className="p-1.5 text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 rounded transition-colors"
                                            title="D&eacute;finir comme principal"
                                        >
                                            <Star className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveOccupation(occupation.id);
                                        }}
                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                        title="Supprimer"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {expandedOccupation === occupation.id ? (
                                    <ChevronUp className="w-5 h-5 text-gray-400" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                            </div>

                            {/* Skills panel */}
                            {expandedOccupation === occupation.id && (
                                <div className="px-4 pb-4">
                                    {loadingSkills[occupation.id] ? (
                                        <div className="flex items-center justify-center py-6">
                                            <Loader2 className="w-5 h-5 text-cabaza-purple animate-spin mr-2" />
                                            <span className="text-sm text-gray-500">
                                                Chargement des comp&eacute;tences ESCO...
                                            </span>
                                        </div>
                                    ) : occupationSkills[occupation.id]?.length > 0 ? (
                                        <div className="space-y-3">
                                            {/* Essential skills */}
                                            {occupationSkills[occupation.id].filter(s => s.isEssential).length > 0 && (
                                                <div>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Sparkles className="w-4 h-4 text-amber-500" />
                                                        <span className="text-sm font-medium text-gray-700">
                                                            Comp&eacute;tences essentielles
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {occupationSkills[occupation.id]
                                                            .filter(s => s.isEssential)
                                                            .map(skill => (
                                                                <SkillChip
                                                                    key={skill.id}
                                                                    skill={skill}
                                                                    language={language}
                                                                    isSelected={userSkillIds.includes(skill.id)}
                                                                    isLoading={togglingSkill === skill.id}
                                                                    onClick={() => handleToggleSkill(skill.id, occupation.id)}
                                                                />
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            )}

                                            {/* Optional skills */}
                                            {occupationSkills[occupation.id].filter(s => !s.isEssential).length > 0 && (
                                                <div>
                                                    <div className="flex items-center gap-2 mb-2 mt-4">
                                                        <Check className="w-4 h-4 text-gray-400" />
                                                        <span className="text-sm font-medium text-gray-700">
                                                            Comp&eacute;tences optionnelles
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {occupationSkills[occupation.id]
                                                            .filter(s => !s.isEssential)
                                                            .map(skill => (
                                                                <SkillChip
                                                                    key={skill.id}
                                                                    skill={skill}
                                                                    language={language}
                                                                    isSelected={userSkillIds.includes(skill.id)}
                                                                    isLoading={togglingSkill === skill.id}
                                                                    onClick={() => handleToggleSkill(skill.id, occupation.id)}
                                                                />
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 py-4 text-gray-500">
                                            <AlertCircle className="w-5 h-5" />
                                            <span>Aucune comp&eacute;tence disponible pour ce m&eacute;tier</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

/**
 * SkillChip - Individual skill button/chip
 * Automatically fetches missing translation from ESCO API
 */
const SkillChip = ({ skill, language, isSelected, isLoading, onClick }) => {
    const [displayName, setDisplayName] = useState('');
    const [hasFetched, setHasFetched] = useState(false);

    useEffect(() => {
        // Get the name in the requested language
        const nameInLang = skill[`name_${language}`];
        const fallbackName = skill.name_fr || skill.name_en || '';

        if (nameInLang && nameInLang.trim().length > 0) {
            // Translation exists
            setDisplayName(nameInLang);
        } else if (fallbackName) {
            // Show fallback immediately
            setDisplayName(fallbackName);

            // Fetch translation in background if not already fetched
            if (!hasFetched) {
                setHasFetched(true);
                console.log(`Fetching translation for skill ${skill.id} in ${language}`);

                fillSkillTranslation(skill.id, language)
                    .then(result => {
                        console.log(`Translation result for ${skill.id}:`, result);
                        if (result.success && result.name) {
                            setDisplayName(result.name);
                        }
                    })
                    .catch(err => console.error('Error fetching translation:', err));
            }
        }
    }, [skill.id, language, hasFetched]);

    return (
        <button
            onClick={onClick}
            disabled={isLoading}
            title={displayName}
            className={`
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                transition-all duration-200 border group/chip relative
                ${isSelected
                    ? 'bg-cabaza-purple text-white border-cabaza-purple'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-cabaza-purple hover:text-cabaza-purple'
                }
                ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
            `}
        >
            {isLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
            ) : isSelected ? (
                <Check className="w-3 h-3" />
            ) : null}
            <span className="truncate max-w-[200px]">{displayName}</span>
            {/* Tooltip */}
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover/chip:opacity-100 group-hover/chip:visible transition-all duration-200 pointer-events-none z-50 shadow-lg">
                {displayName}
                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></span>
            </span>
        </button>
    );
};

export default OccupationsSkillsPanel;
